import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const APP_NAME = "Evidence Labs Research";

// Route-prefix → i18n key inside the ``app`` namespace. The hook walks the
// list once per route change and picks the FIRST entry whose `prefix` matches
// the current pathname. Unmatched routes fall back to the bare app name.
const ROUTE_PREFIXES = [
  { prefix: "/dashboard", key: "title.dashboard" },
  { prefix: "/brands", key: "title.brands" },
  { prefix: "/products", key: "title.products" },
  { prefix: "/competitors", key: "title.competitors" },
  { prefix: "/evidence", key: "title.evidence" },
  { prefix: "/findings", key: "title.findings" },
  { prefix: "/projects", key: "title.projects" },
  { prefix: "/reports", key: "title.reports" },
  { prefix: "/settings", key: "title.settings" },
  { prefix: "/invite", key: "title.invite" },
  { prefix: "/login", key: "title.login" },
  { prefix: "/signup", key: "title.signup" },
];

/**
 * Routes whose page components manage their own <title> via useEffect (the
 * marketing landing + legal stubs). We skip them here so the parent-level
 * DocumentTitleEffect doesn't overwrite what those pages wrote.
 */
const SELF_MANAGED_TITLE_PATHS = ["/legal"];
const SELF_MANAGED_TITLE_EXACT = new Set(["/"]);

/**
 * Drives the browser tab title from the current route + UI language.
 *
 * Mount this hook once at the top of the app (inside <Router>). It updates
 * ``document.title`` whenever the route or i18n language changes. The title
 * format is "{section} · Evidence Labs Research". Sections fall back to bare
 * "Evidence Labs Research" when no prefix matches.
 *
 * Per the spec, the AppName itself stays Latin — only the section prefix is
 * localised (so HU users see "Márkák · Evidence Labs Research").
 */
export function useDocumentTitle() {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation("common");

  useEffect(() => {
    // Pages that own their own title (Landing, Legal) opt out via
    // SELF_MANAGED_TITLE_PATHS/EXACT — don't clobber what they set.
    if (SELF_MANAGED_TITLE_EXACT.has(pathname)) return;
    if (SELF_MANAGED_TITLE_PATHS.some((p) => pathname.startsWith(p))) return;

    const match = ROUTE_PREFIXES.find((r) => pathname.startsWith(r.prefix));
    if (!match) {
      document.title = APP_NAME;
      return;
    }
    const section = t(match.key, { defaultValue: "" });
    document.title = section ? `${section} · ${APP_NAME}` : APP_NAME;
  }, [pathname, i18n.language, t]);
}

export const APP_TITLE = APP_NAME;
